// Menu behaviour for the shell's three menus — product, account, activity picker — as
// docs/DESIGN.md specifies it (Accessibility): opening moves focus to the first item, Arrow Up and
// Arrow Down move between items, Esc closes and returns focus to the button, a click outside or a
// choice closes it, and only one menu is open at a time.

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";

const ITEMS = '[role^="menuitem"],[role="option"]';
const OPENED = "preva:menu-opened";

export function useMenu(id: string) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((refocus = false) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  }, []);

  // One at a time: opening any menu announces itself, and every other menu closes.
  useEffect(() => {
    const onOther = (e: Event) => { if ((e as CustomEvent<string>).detail !== id) setOpen(false); };
    window.addEventListener(OPENED, onOther);
    return () => window.removeEventListener(OPENED, onOther);
  }, [id]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>(ITEMS)?.focus();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggle = useCallback(() => {
    if (!open) window.dispatchEvent(new CustomEvent(OPENED, { detail: id }));
    setOpen(!open);
  }, [open, id]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>(ITEMS) ?? []);
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === "ArrowDown" && items.length) {
      e.preventDefault(); items[(i + 1) % items.length].focus();
    } else if (e.key === "ArrowUp" && items.length) {
      e.preventDefault(); items[(i - 1 + items.length) % items.length].focus();
    } else if (e.key === "Home" && items.length) {
      e.preventDefault(); items[0].focus();
    } else if (e.key === "End" && items.length) {
      e.preventDefault(); items[items.length - 1].focus();
    } else if (e.key === "Escape") {
      e.preventDefault(); close(true);
    } else if (e.key === "Tab") {
      close();
    }
  }, [close]);

  return {
    open, close, toggle,
    buttonProps: {
      ref: buttonRef, onClick: toggle, "aria-expanded": open,
      "aria-controls": `${id}-menu`,
    },
    menuProps: { ref: menuRef, id: `${id}-menu`, hidden: !open, onKeyDown },
  };
}
